import { Compass, MessageSquare, BarChart3, GraduationCap, Heart } from "lucide-react";
import { Reveal, PageHero, SectionHead } from "@/components/shared";
import { CTABand } from "@/pages/Home";
import { IMAGES } from "@/lib/content";

export default function About() {
  const philosophy = [
    { icon: Compass, title: "Frictionless above all", text: "Every tap removed is a customer kept. We design for a guest on hotel Wi-Fi with one hand on a rum punch." },
    { icon: MessageSquare, title: "Plain English, always", text: "If we cannot explain a technology decision over coffee at Hemingway's, we do not make it." },
    { icon: BarChart3, title: "Measurable Caribbean ROI", text: "Bookings, hours saved, margin protected. If a project cannot be measured, we will tell you not to buy it." },
  ];

  const team = [
    { name: "Andrew Albert Owusu-Ansah Fui", role: "Founder & Principal Consultant", img: IMAGES.founder, note: "On-island, in the room, accountable. Every engagement starts with a face-to-face conversation.", founder: true },
    { name: "Regional Delivery Team", role: "Engineering, design, automation", img: IMAGES.meeting, note: "Senior specialists across the Caribbean and beyond." },
  ];

  return (
    <main data-testid="about-page">
      <PageHero
        eyebrow="About Elite Imperium Digital"
        title="Born in Antigua. Built for world-class standards."
        lead="The technology consultancy and digital delivery arm of the Elite Imperium Group — helping local and regional businesses win more customers and cut the admin that slows them down."
        image={IMAGES.office}
      />

      <section className="pb-24 lg:pb-32" data-testid="story-section">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-2 lg:px-10">
          <div>
            <Reveal><p className="eyebrow">Our story</p></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 font-serif text-3xl tracking-tight text-ivory sm:text-4xl" style={{ lineHeight: 1.2 }}>
                Island knowledge should never mean island standards.
              </h2>
            </Reveal>
            <Reveal delay={0.18}>
              <div className="mt-6 space-y-5 text-base font-light leading-relaxed text-slate-300">
                <p>
                  Antigua and Barbuda hosts some of the finest hospitality brands on earth — yet too many of them hand a
                  fifth of their revenue to overseas platforms because booking direct is harder than it should be.
                </p>
                <p>
                  Elite Imperium Digital exists to close that gap. We pair a founder who lives and works on the island
                  with a senior regional delivery team, so you get someone who can walk your property on a Tuesday and a
                  build that stands beside any international agency's work.
                </p>
                <p>
                  One contact for everything: your website, your payments, your automation, your hosting. When something
                  matters to your season, you call one number — ours.
                </p>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="absolute -inset-3 rounded-3xl border border-gold/25" />
              <img src={IMAGES.meeting} alt="The Elite Imperium Digital team in session" className="relative h-[420px] w-full rounded-3xl object-cover duotone" />
              <div className="absolute -bottom-6 -right-4 rounded-2xl border border-gold/30 bg-navy-deep/90 px-6 py-4 backdrop-blur-xl sm:-right-8">
                <p className="font-serif text-2xl text-gold-glow">Est. Antigua</p>
                <p className="mt-1 text-xs uppercase tracking-widest text-slate-400">Elite Imperium Group</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-gold/10 bg-navy-deep/50 py-24 lg:py-32" data-testid="philosophy-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <SectionHead eyebrow="How we work" title="Three principles, zero jargon" />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {philosophy.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.12}>
                <div className="gold-ring-card h-full p-8" data-testid={`philosophy-card-${i}`}>
                  <div className="flex h-12 w-12 items-center justify-center rounded-full border border-gold/30 bg-gold/10">
                    <p.icon className="h-5 w-5 text-gold-glow" />
                  </div>
                  <h3 className="mt-6 font-serif text-xl text-ivory">{p.title}</h3>
                  <p className="mt-3 text-sm font-light leading-relaxed text-slate-400">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-gold/10 py-24 lg:py-32" data-testid="founder-section">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-12 lg:px-10">
          <Reveal className="lg:col-span-5">
            <div className="relative mx-auto max-w-md">
              <div className="absolute -inset-3 rounded-3xl border border-gold/25" />
              <img src={IMAGES.founder} alt="Andrew Albert Owusu-Ansah Fui, Founder of Elite Imperium Digital" className="relative h-[480px] w-full rounded-3xl object-cover object-top" data-testid="founder-portrait" />
              <div className="absolute -bottom-6 left-6 rounded-2xl border border-gold/30 bg-navy-deep/90 px-6 py-4 backdrop-blur-xl">
                <p className="font-serif text-lg text-gold-glow">Andrew Albert Owusu-Ansah Fui</p>
                <p className="mt-0.5 text-[11px] uppercase tracking-widest text-slate-400">Founder &amp; Principal Consultant</p>
              </div>
            </div>
          </Reveal>
          <div className="lg:col-span-7">
            <Reveal><p className="eyebrow">Meet the founder</p></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 font-serif text-3xl tracking-tight text-ivory sm:text-4xl" style={{ lineHeight: 1.2 }}>
                When you call, you speak to him directly.
              </h2>
            </Reveal>
            <Reveal delay={0.18}>
              <div className="mt-6 space-y-5 text-base font-light leading-relaxed text-slate-300" data-testid="founder-bio">
                <p>
                  Andrew Albert Owusu-Ansah Fui is the founder and CEO of Elite Imperium Corp &amp; Group, a
                  UK-registered company working across technology, real estate and creative solutions, also registered
                  in the USA, Ghana and Germany. Elite Imperium Digital is its technology consultancy.
                </p>
                <p>
                  Before founding the Group, Andrew Albert worked with some of the world's best-known technology and
                  communications companies — including Apple, Amazon and Vodafone — where he saw first-hand how large
                  organisations choose, build and protect their technology.
                </p>
                <p>
                  With a heart and love for Antigua, he now lives and works on the island. He started Elite Imperium
                  Digital to give local businesses that same standard of advice and delivery — in plain language, at a
                  fair, fixed price.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.26}>
              <div className="mt-8 flex flex-wrap gap-3">
                {["Apple", "Amazon", "Vodafone"].map((c) => (
                  <span key={c} className="rounded-full border border-gold/25 bg-gold/5 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-gold-glow">
                    {c}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32" data-testid="team-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <SectionHead eyebrow="Who you'll work with" title="A small team, deliberately" />
          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {team.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.12}>
                <div className="gold-ring-card group overflow-hidden" data-testid={`team-card-${i}`}>
                  <div className={`relative overflow-hidden ${t.founder ? "h-[420px]" : "h-64"}`}>
                    <img src={t.img} alt={t.name} className={`h-full w-full object-cover transition-transform duration-700 group-hover:scale-105 ${t.founder ? "object-top" : "duotone"}`} />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-surface to-transparent" />
                  </div>
                  <div className="p-8">
                    <h3 className="font-serif text-2xl text-ivory">{t.name}</h3>
                    <p className="mt-1 font-mono text-xs uppercase tracking-widest text-gold/80">{t.role}</p>
                    <p className="mt-4 text-sm font-light text-slate-400">{t.note}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-gold/10 bg-navy-deep/50 py-24" data-testid="community-section">
        <div className="mx-auto max-w-4xl px-6 text-center lg:px-10">
          <Reveal>
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-gold/30 bg-gold/10">
              <Heart className="h-6 w-6 text-gold-glow" />
            </div>
            <h2 className="mt-6 font-serif text-3xl tracking-tight text-ivory sm:text-4xl">Committed to the islands we serve</h2>
            <p className="mx-auto mt-5 max-w-2xl text-base font-light leading-relaxed text-slate-300">
              We mentor young Antiguan and Barbudan talent, run free digital clinics for small businesses each quarter,
              and keep every dollar of our delivery work circulating in the regional economy wherever we can.
            </p>
            <div className="mt-8 flex items-center justify-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-gold/70">
              <GraduationCap className="h-4 w-4" /> Quarterly digital clinics — Antigua
            </div>
          </Reveal>
        </div>
      </section>

      <CTABand
        title="Let's build something the island is proud of."
        text="A free consultation is the easiest first step — thirty minutes, plain English, zero obligation."
      />
    </main>
  );
}
